import React, { useState } from 'react';
import { Wrench, Settings, Home, Headphones, RefreshCw, MessageCircle, ArrowRight, Check, AlertTriangle } from 'lucide-react';
import { services, images } from '../data/mock';

const iconMap = {
  Wrench,
  Settings,
  Home,
  Headphones,
  RefreshCw,
  MessageCircle
};

// Image différente pour chaque service
const serviceImages = [
  images.serviceRepair,
  images.serviceInstallation,
  images.serviceSupport,
  images.serviceTransfer,
  images.serviceAdvice,
  images.serviceMaintenance
];

const ServicesSection = () => {
  const [activeService, setActiveService] = useState(0);

  return (
    <section id="services" className="py-24 bg-white dark:bg-gray-900 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary-cyan/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary-blue/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-2 bg-primary-cyan/10 text-primary-blue dark:text-primary-cyan font-semibold rounded-full text-sm mb-4">
            Nos Services
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            Un accompagnement
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-blue to-primary-cyan"> complet</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300">
            Bien plus qu'une simple vente : réparation, installation, formation et assistance gratuite à vie.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Service Cards */}
          <div className="grid sm:grid-cols-2 gap-4">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              const isActive = activeService === index;
              
              return (
                <div
                  key={service.id}
                  onClick={() => setActiveService(index)}
                  className={`p-6 rounded-2xl cursor-pointer transition-all duration-300 relative ${
                    isActive
                      ? 'bg-gradient-to-br from-primary-blue to-primary-blue/90 text-white shadow-xl scale-[1.02]'
                      : 'bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white'
                  } ${service.highlight ? 'ring-2 ring-primary-cyan ring-offset-2 dark:ring-offset-gray-900' : ''}`}
                >
                  {service.highlight && !isActive && (
                    <span className="absolute -top-2 -right-2 bg-primary-cyan text-white text-xs px-2 py-1 rounded-full font-semibold">
                      ⭐ Exclusif
                    </span>
                  )}
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                    isActive ? 'bg-white/20' : 'bg-primary-cyan/10'
                  }`}>
                    <IconComponent className={`w-6 h-6 ${isActive ? 'text-white' : 'text-primary-blue dark:text-primary-cyan'}`} />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{service.title}</h3>
                  <p className={`text-sm ${isActive ? 'text-white/80' : 'text-gray-600 dark:text-gray-400'}`}>
                    {service.description.substring(0, 80)}...
                  </p>
                </div>
              );
            })}
          </div>

          {/* Active Service Detail */}
          <div className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 rounded-3xl p-8 lg:p-10 border border-gray-100 dark:border-gray-700 shadow-xl">
            <div className="relative mb-6 rounded-2xl overflow-hidden">
              <img
                src={serviceImages[activeService]}
                alt={services[activeService].title}
                className="w-full h-64 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-blue/40 to-transparent" />
              {services[activeService].highlight && (
                <div className="absolute top-4 right-4 bg-primary-cyan text-white px-4 py-2 rounded-full font-semibold text-sm">
                  ⭐ Service exclusif CLIC58
                </div>
              )}
            </div>

            <span className="inline-block px-3 py-1 bg-primary-cyan/10 text-primary-blue dark:text-primary-cyan font-semibold rounded-full text-sm mb-4">
              Service #{activeService + 1}
            </span>
            
            <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-4">
              {services[activeService].title}
            </h3>
            
            <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
              {services[activeService].description}
            </p>

            <div className="space-y-3 mb-6">
              {services[activeService].features.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-primary-cyan/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-primary-cyan" />
                  </div>
                  <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                </div>
              ))}
            </div>

            {/* Note if exists */}
            {services[activeService].note && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl p-4 mb-6 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-amber-800 dark:text-amber-200 text-sm">{services[activeService].note}</p>
              </div>
            )}

            <a
              href="#contact"
              className="inline-flex items-center gap-2 text-primary-blue dark:text-primary-cyan font-semibold hover:gap-4 transition-all duration-300"
            >
              Demander un devis
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
